import React, { useState, useRef, SyntheticEvent, KeyboardEvent, useEffect } from 'react';
import {
  Box,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  InputBase,
} from '@tonic-ui/react';
import { FieldArray, useField, FieldHookConfig } from 'formik';
import { useTranslation } from 'react-i18next';
import EditableTag from './editable-tag';
import { useThemeColorStyle } from '../../../hooks/use-theme-color-style';

type TagsProps = FieldHookConfig<string> & {
  placeholder?: string;
  id: string;
};

const Tags = ({ placeholder, id, ...rest }: TagsProps): JSX.Element => {
  const colorStyle = useThemeColorStyle();
  const {
    placeholderColor,
    invalidBorderColor,
    errorText,
    focusBorderColor,
    borderColor,
  } = colorStyle.support;
  const { t } = useTranslation();
  const tagCreatorRef = useRef<HTMLInputElement>();
  const [inputValue, setInputValue] = useState('');
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [isWrapperFocused, setWrapperFocused] = useState(false);

  const [field, meta] = useField(rest);

  const handleWrapperClick = () => {
    tagCreatorRef?.current?.focus();
    setWrapperFocused(true);
  };

  useEffect(() => {
    setShowPlaceholder(false);
  }, [field?.value.length > 0]);

  return (
    <FieldArray
      name={rest?.name}
      render={(arrayHelpers) => {
        const fieldValue = field?.value || [];
        const { error = [] } = meta;
        const tagErrors: string[] | string = error;

        const hasError: boolean = Array.isArray(tagErrors)
          ? tagErrors.some((error) => !!error)
          : !!tagErrors; // validation for Tags input field
        const showError = hasError && tagErrors !== ' ';

        const sanitizeTagInput = (event) => {
          return (event.target as HTMLInputElement).value.trim() as string;
        };

        const handleInputKeyUp = ({ target, key }: KeyboardEvent) => {
          const tagValue = ((target as HTMLInputElement)?.value.toLowerCase() ?? '').replaceAll(
            /[,;]/g,
            ''
          ) as string;

          if (!tagValue.length) {
            return;
          }
          switch (key) {
            case 'Enter':
              arrayHelpers.push(tagValue);
              setInputValue('');
              break;
            case ',':
            case ';':
              arrayHelpers.push(tagValue);
              setInputValue('');
              break;
            case 'Escape':
              setInputValue('');
              break;
          }
        };

        const handleInputBlur = (event: SyntheticEvent<HTMLInputElement, MouseEvent>) => {
          event.stopPropagation();
          setWrapperFocused(false);

          const tagValue = sanitizeTagInput(event);
          if (tagValue) {
            arrayHelpers.push(tagValue);
            setInputValue('');
          }
          if (!fieldValue.length && !tagValue) {
            setShowPlaceholder(true);
          }
        };

        const handleInputFocus = () => {
          setShowPlaceholder(false);
        };

        const handleInputPaste = (event: ClipboardEvent) => {
          event.preventDefault();
          const pastedData = event?.clipboardData?.getData('text') as string;
          const separatedValues = pastedData.split(
            new RegExp([',', ';', '\n', '\r', '\r\n'].join('|'))
          ) as string[];
          separatedValues.forEach((tagValue: string) => {
            if (tagValue) arrayHelpers.push(tagValue.toLowerCase());
          });
          setInputValue('');
        };

        const handleInputChange = (event: SyntheticEvent) => {
          setInputValue(sanitizeTagInput(event));
        };

        const handleTagClose = (tagIndex: number) => {
          arrayHelpers.remove(tagIndex);
          tagCreatorRef.current?.focus();
        };

        const handleTagChange = (tagIndex: number) => (event: SyntheticEvent) => {
          arrayHelpers.replace(tagIndex, sanitizeTagInput(event));
        };

        const handleTagFocus = () => {
          setWrapperFocused(true);
        };

        return (
          <>
            <Box
              backgroundColor="transparent"
              borderRadius="sm"
              fontSize="sm"
              lineHeight="sm"
              px="3x"
              pb="1x"
              border="solid 1px"
              borderColor={isWrapperFocused ? focusBorderColor : borderColor}
              _invalid={{ borderColor: invalidBorderColor }}
              role="presentation"
              data-id="support-form-tags-email-field"
              aria-invalid={showError}
              onClick={handleWrapperClick}
            >
              {fieldValue.map((tagValue, id) => {
                const errorMessage: string = hasError && tagErrors[id] ? t(`${tagErrors[id]}`) : '';

                return (
                  <EditableTag
                    key={id}
                    value={tagValue?.toLowerCase()}
                    error={!!errorMessage}
                    onChange={handleTagChange(id)}
                    onClose={() => handleTagClose(id)}
                    onFocus={handleTagFocus}
                  >
                    {errorMessage ? (
                      <Popover hideArrow trigger="hover">
                        <PopoverTrigger>
                          <Text>{tagValue}</Text>
                        </PopoverTrigger>
                        <PopoverContent>
                          <PopoverBody>{errorMessage}</PopoverBody>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      tagValue
                    )}
                  </EditableTag>
                );
              })}
              {showPlaceholder && <Text color={placeholderColor}>{placeholder}</Text>}
              <InputBase
                data-id={`tags-${rest?.name}-wrapper-input`}
                ref={tagCreatorRef}
                id={id}
                minHeight="6x"
                mt="1x"
                value={inputValue}
                onChange={handleInputChange}
                onKeyUp={handleInputKeyUp}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onPaste={handleInputPaste}
              />
            </Box>
            {showError && <Text color={errorText}>{t('validations.supportFormEmailFormat')}</Text>}
          </>
        );
      }}
    />
  );
};

Tags.displayName = 'Tags';

export default Tags;
